import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormField } from './FormField';
import { evaluatePassword } from '../utils';

interface PasswordFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  hint?: string;
  required?: boolean;
  autoComplete?: string;
  /** Exibe a barra de força da senha. */
  showStrength?: boolean;
  id?: string;
}

/** Campo de senha com alternância de visibilidade e medidor de força opcional. */
export function PasswordField({
  label,
  value,
  onChange,
  error,
  hint,
  required,
  autoComplete = 'new-password',
  showStrength = false,
  id,
}: PasswordFieldProps) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const strength = evaluatePassword(value);
  const strengthLabel = t(`views.registration.password_strength.${strength.score}`, strength.label);

  return (
    <div>
      <FormField
        id={id}
        label={label}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        error={error}
        hint={hint}
        required={required}
        icon="fa-lock"
        autoComplete={autoComplete}
        trailing={
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            aria-label={visible ? t('views.registration.hide_password', 'Ocultar senha') : t('views.registration.show_password', 'Mostrar senha')}
            aria-pressed={visible}
            className="shrink-0 px-[14px] h-full border-0 bg-transparent cursor-pointer text-[15px] text-mist-500 hover:text-navy-800"
          >
            <i className={`fa ${visible ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden="true"></i>
          </button>
        }
      />
      {showStrength && value && (
        <div className="-mt-[8px] mb-4" aria-live="polite">
          <div className="flex gap-[4px]" role="presentation">
            {[1, 2, 3, 4].map((i) => (
              <span
                key={i}
                className="h-[4px] flex-1 rounded-full transition-colors"
                style={{ backgroundColor: i <= strength.score ? strength.color : '#EDF5F7' }}
              />
            ))}
          </div>
          <p className="mt-[4px] text-[11px]" style={{ color: strength.color }}>
            {t('views.registration.strength_prefix', 'Força:')} {strengthLabel}
          </p>
        </div>
      )}
    </div>
  );
}
